'use client';

import { AtSymbolIcon, LockClosedIcon, ExclamationTriangleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import Image from 'next/image';
import { signIn } from 'next-auth/react';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import EvkLoader from './loader';

export default function LoginForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const searchParams = useSearchParams();
  const error = searchParams.get('error');

  useEffect(() => {
    if (error) {
      switch (error) {
        case 'CustomError:UserNotFound':
          setErrorMessage('O e-mail associado a esta conta Google não está cadastrado no nosso sistema.');
          break;
        case 'CustomError:DatabaseError':
          setErrorMessage('Ocorreu um problema no servidor. Por favor, tente novamente mais tarde.');
          break;
        case 'Configuration':
          setErrorMessage('E-mail ou senha inválidos.');
          break;
        case 'AccessDenied':
          setErrorMessage('Seu e-mail não possui permissão de acesso ao sistema.');
          break;
        default:
          setErrorMessage('Ocorreu um problema no servidor. Por favor, tente novamente.');
          break;
      }
    }
  }, [error]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    const data = new FormData(event.currentTarget);

    try {
      const result = await signIn('credentials', {
        redirect: true,
        email: data.get('email'),
        password: data.get('password')
      });

      if (result?.error) {
        setErrorMessage('E-mail ou senha incorretos.');
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('Erro ao tentar realizar login.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      {isLoading && <EvkLoader />}

      <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-2xl border border-slate-100 space-y-6 relative overflow-hidden">
        {/* Topo do Form com Logo Mobile e Boas-Vindas */}
        <div className="text-center space-y-3">
          <div className="flex justify-center mb-2">
            <Image src="/Logo.png" width={200} height={50} alt="Evolution Logo" className="h-16 w-auto object-contain" />
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">Acesse sua Conta</h2>
            <p className="text-xs text-slate-500 mt-1">Informe suas credenciais registradas para acessar o sistema</p>
          </div>
        </div>

        {/* Mensagem de Erro com Alerta Estilizado */}
        {errorMessage && (
          <div className="p-3.5 bg-rose-50 border border-rose-200/80 rounded-2xl flex items-start gap-2.5 animate-fadeIn">
            <ExclamationTriangleIcon className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <p className="text-xs font-semibold text-rose-700 leading-snug">{errorMessage}</p>
          </div>
        )}

        {/* Campos de Input */}
        <div className="space-y-4">
          {/* E-mail */}
          <div className="space-y-1.5">
            <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
              E-Mail de Acesso
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                name="email"
                placeholder="seu.email@clinicaevollutionkids.com.br"
                required
                className="w-full rounded-xl border border-slate-300 bg-slate-50/50 pl-10 pr-3.5 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
              />
              <AtSymbolIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            </div>
          </div>

          {/* Senha com Eye Toggle */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                Senha
              </label>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="••••••••"
                required
                minLength={6}
                className="w-full rounded-xl border border-slate-300 bg-slate-50/50 pl-10 pr-10 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
              />
              <LockClosedIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1 cursor-pointer"
              >
                {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Ações de Entrada */}
        <div className="space-y-4 pt-2">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-bold shadow-md shadow-blue-500/25 transition-all hover:scale-[1.01] active:scale-[0.99] cursor-pointer disabled:opacity-50"
          >
            <span>Entrar no Sistema</span>
            <ArrowRightIcon className="w-4 h-4" />
          </button>

          {/* Divisor */}
          <div className="relative flex items-center justify-center py-2">
            <div className="w-full border-t border-slate-200" />
            <span className="absolute bg-white px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              ou acesse com
            </span>
          </div>

          {/* Google Login */}
          <div className="w-full">
            <GoogleLoginButton
              onClick={() => {
                setIsLoading(true);
                signIn('google');
              }}
            />
          </div>
        </div>
      </div>
    </form>
  );
}
